/**
 * errorStateProps - the single kind → presentation mapping for every error
 * surface in the app (issue: error-state system, spec
 * docs/superpowers/specs/2026-07-28-error-state-system-design.md).
 *
 * One function, two consumers, so the full-page (A) and inline (B) treatments
 * can never drift apart:
 *   A  <PageState {...errorStateProps(err, { noun: 'policies' })} />
 *   B  <InlineError error={err} noun="policies" onRetry={refetch} />
 *
 * Design rules encoded here (from the approved plan):
 *   1. Name the cause. "Couldn't load X" for a 403 is a lie by omission.
 *   2. Retry must be honest - `retryable` is false for permission / missing /
 *      conflict kinds, so no surface renders a button that cannot succeed.
 *   3. Permission errors render `state="denied"` (warn + lock), not `error`.
 *   4. Never invent a countdown. `rate_limit` copy only names seconds when the
 *      server actually sent Retry-After (ApiError.retryAfterSeconds).
 *   5. 403 has no self-serve "Request access" endpoint in conmap today, so the
 *      copy tells the user who to ask instead of rendering a dead button.
 */
import type { ComponentType } from 'react';
import { ApiError, type ApiErrorKind } from '@shared/api/errors';
import {
  IconAlert,
  IconClock,
  IconErrorCircle,
  IconGlobe,
  IconKey,
  IconLock,
  IconSearch,
  IconServer,
  IconUsers,
  IconZap,
  type IconProps,
} from '@shared/icons';
import type { PageStateKind } from './PageState';

/** What the caller knows about the failing surface. */
export type ErrorStateContext = {
  /**
   * Plural noun for the thing that failed to load, lower-case: 'policies',
   * 'agents', 'MCP servers'. Used to name the cause instead of "this page".
   */
  noun?: string;
  /** Singular noun for detail routes: 'policy', 'agent'. Falls back to `noun`. */
  singular?: string;
  /**
   * Label for the navigate-away action on `not_found` (e.g. 'Back to policies').
   * When omitted, no navigate CTA label is produced.
   */
  backLabel?: string;
};

export type ErrorStatePresentation = {
  /** Which PageState visual to use - `denied` for permission kinds. */
  state: Extract<PageStateKind, 'error' | 'denied'>;
  kind: ApiErrorKind | 'crash';
  icon: ComponentType<IconProps>;
  headline: string;
  body: string;
  /**
   * True only when retrying could actually succeed. Permission, missing, and
   * conflict kinds are false - the caller must not render a retry for them.
   */
  retryable: boolean;
  /** Label for the retry affordance when `retryable`. */
  retryLabel: string;
  /**
   * Non-retry primary action the caller should offer instead, when one exists
   * and is real. `null` when the only honest thing is copy (e.g. 403 today).
   */
  action: { kind: 'reauth' | 'navigate' | 'reload'; label: string } | null;
  /** Seconds the server told us to wait; null unless it really said so. */
  retryAfterSeconds: number | null;
};

const ICONS: Record<ApiErrorKind | 'crash', ComponentType<IconProps>> = {
  network: IconGlobe,
  server: IconServer,
  rate_limit: IconClock,
  unknown: IconAlert,
  forbidden: IconLock,
  unauthorized: IconKey,
  not_found: IconSearch,
  validation: IconErrorCircle,
  conflict: IconUsers,
  crash: IconZap,
};

/** "policies" → "Couldn't load policies"; undefined → "Couldn't load this page". */
function target(ctx: ErrorStateContext): string {
  return ctx.noun ?? 'this page';
}

/**
 * Server-supplied detail, when it is fit to show a user. NestJS 500s often
 * carry framework noise ("Internal server error") that adds nothing, so it is
 * only surfaced when it differs from the generic fallbacks.
 */
function serverDetail(error: ApiError): string | null {
  const raw = error.message?.trim();
  if (!raw) return null;
  const noise = /^(internal server error|bad request|unauthorized|forbidden|not found)$/i;
  if (noise.test(raw)) return null;
  if (raw.startsWith('API ')) return null; // our own synthetic fallback
  if (/^Cannot (GET|POST|PUT|PATCH|DELETE) /i.test(raw)) return null; // Nest 404 noise
  return raw;
}

export function errorStateProps(
  error: unknown,
  ctx: ErrorStateContext = {}
): ErrorStatePresentation {
  // No error at all. Callers commonly guard with `isError || !data`, so this is
  // the "request succeeded but produced nothing renderable" path - a missing
  // resource, not a crash. Reported as not_found so the copy stays honest and
  // the surface can offer a navigate-away instead of a pointless retry.
  if (error === null || error === undefined) {
    const thing = ctx.singular ?? ctx.noun ?? 'page';
    return {
      state: 'error',
      kind: 'not_found',
      icon: ICONS.not_found,
      headline: `That ${thing} doesn’t exist`,
      body: 'It may have been deleted, or the link you followed is out of date.',
      retryable: false,
      retryLabel: '',
      action: ctx.backLabel ? { kind: 'navigate', label: ctx.backLabel } : null,
      retryAfterSeconds: null,
    };
  }

  // Anything else that is not an ApiError reached us from a render throw or a
  // non-network bug - treat it as a crash, not a load failure.
  if (!(error instanceof ApiError)) {
    return {
      state: 'error',
      kind: 'crash',
      icon: ICONS.crash,
      headline: 'This view crashed',
      body: 'A bug in the interface stopped this section from rendering. The rest of the app is unaffected.',
      retryable: true,
      retryLabel: 'Reload view',
      // No separate action: the retry above already remounts the view, and a
      // second identically-labelled button is just a duplicate.
      action: null,
      retryAfterSeconds: null,
    };
  }

  const detail = serverDetail(error);
  const base = {
    kind: error.kind,
    icon: ICONS[error.kind],
    retryAfterSeconds: error.retryAfterSeconds,
  } as const;

  switch (error.kind) {
    case 'network':
      return {
        ...base,
        state: 'error',
        headline: 'You appear to be offline',
        body: `We can’t reach ArmorIQ to load ${target(ctx)}. Check your connection and try again.`,
        retryable: true,
        retryLabel: 'Try again',
        action: null,
      };

    case 'server':
      return {
        ...base,
        state: 'error',
        headline: 'Something broke on our end',
        body:
          detail ??
          `This isn’t your fault - the request for ${target(ctx)} failed server-side. Try again in a moment.`,
        retryable: true,
        retryLabel: 'Try again',
        action: null,
      };

    case 'rate_limit': {
      const secs = error.retryAfterSeconds;
      return {
        ...base,
        state: 'error',
        headline: 'Too many requests',
        body: secs
          ? `You’ve hit the rate limit. Try again in ${secs} second${secs === 1 ? '' : 's'}.`
          : 'You’ve hit the rate limit for this endpoint. Try again shortly.',
        retryable: true,
        retryLabel: secs ? `Retry in ${secs}s` : 'Try again',
        action: null,
      };
    }

    case 'forbidden':
      return {
        ...base,
        state: 'denied',
        headline: `You don’t have access to ${target(ctx)}`,
        // No self-serve access-request endpoint exists in conmap, so we name
        // who can fix it rather than render a button that does nothing.
        body: `Your role in this organization can’t view ${target(ctx)}. Contact your admin to request access.`,
        retryable: false,
        retryLabel: '',
        action: null,
      };

    case 'unauthorized':
      return {
        ...base,
        state: 'denied',
        headline: 'Your session expired',
        body: 'For your security we signed you out after a period of inactivity. Sign in again to pick up where you left off.',
        retryable: false,
        retryLabel: '',
        action: { kind: 'reauth', label: 'Sign in again' },
      };

    case 'not_found': {
      const thing = ctx.singular ?? ctx.noun ?? 'page';
      return {
        ...base,
        state: 'error',
        headline: `That ${thing} doesn’t exist`,
        body: `It may have been deleted, or the link you followed is out of date. There’s nothing here to retry.`,
        retryable: false,
        retryLabel: '',
        action: ctx.backLabel ? { kind: 'navigate', label: ctx.backLabel } : null,
      };
    }

    case 'validation': {
      const fields = error.fieldErrors();
      const withField = fields.filter((f) => f.field !== '');
      const formLevel = fields.find((f) => f.field === '');
      return {
        ...base,
        state: 'error',
        headline:
          withField.length > 0
            ? `${withField.length} field${withField.length === 1 ? '' : 's'} need${withField.length === 1 ? 's' : ''} attention`
            : 'That change couldn’t be saved',
        body:
          formLevel?.message ??
          detail ??
          'Some values weren’t accepted. Check the highlighted fields and try again.',
        retryable: false,
        retryLabel: '',
        action: null,
      };
    }

    case 'conflict':
      return {
        ...base,
        state: 'error',
        headline: 'Someone else made changes',
        body:
          detail ??
          'Your copy is out of date. Reload to see the current version - saving as-is would overwrite their change.',
        retryable: false,
        retryLabel: '',
        action: { kind: 'reload', label: 'Reload and compare' },
      };

    case 'unknown':
    default:
      return {
        ...base,
        icon: ICONS.unknown,
        state: 'error',
        headline: 'Something went wrong',
        body: detail ?? `We couldn’t load ${target(ctx)}, and couldn’t tell why. Retrying is safe.`,
        retryable: true,
        retryLabel: 'Try again',
        action: null,
      };
  }
}
